// ============================================================
// Shopify Dawn Theme — Product Compare (with MutationObserver fix)
// ============================================================

const COMPARE_KEY   = 'dawn_compare_products';
const COMPARE_LIMIT = 4;

function fixImageUrl(url) {
  if (!url) return '';
  // Already has protocol (http: or https:) — use as-is
  if (url.indexOf('http:') === 0 || url.indexOf('https:') === 0) return url;
  // Protocol-relative (//cdn...) — prepend https:
  if (url.indexOf('//') === 0) return 'https:' + url;
  return url;
}

// ---------- Storage ----------

function isComparePageHandle(handle) {
  return !handle || /compare|product-comparison|product-data/i.test(handle);
}

function getProductHandle(product) {
  if (product.handle && !isComparePageHandle(product.handle)) {
    return product.handle;
  }
  const url = product.url || '';
  const match = url.match(/\/products\/([^/?#]+)/i);
  if (match && !isComparePageHandle(match[1])) {
    return match[1];
  }
  return null;
}

function getProductComparePath(product) {
  const handle = getProductHandle(product);
  return handle ? '/products/' + handle : null;
}

function normalizeCompareProduct(product) {
  if (!product || !product.id) return null;
  const path = getProductComparePath(product);
  if (!path) return null;
  return Object.assign({}, product, { url: path, handle: getProductHandle(product) });
}

function getList() {
  try {
    const list = JSON.parse(localStorage.getItem(COMPARE_KEY)) || [];
    const normalized = list.map(normalizeCompareProduct).filter(Boolean);
    if (normalized.length !== list.length) {
      saveList(normalized);
    }
    return normalized;
  }
  catch { return []; }
}

function saveList(list) {
  localStorage.setItem(COMPARE_KEY, JSON.stringify(list));
}

// ---------- Sync a single checkbox ----------

function syncCheckbox(cb) {
  const ids = getList().map(p => p.id);
  const checked = ids.includes(cb.dataset.productId);
  cb.checked = checked;
  const wrapper = cb.closest('.compare-checkbox-wrapper');
  if (wrapper) {
    const span = wrapper.querySelector('.compare-label-text');
    if (span) span.textContent = checked ? 'Added to compare' : 'Add to compare';
    wrapper.classList.toggle('is-checked', checked);
  }
}

// ---------- Sync ALL checkboxes currently in DOM ----------

function syncAllCheckboxes() {
  document.querySelectorAll('.compare-checkbox').forEach(syncCheckbox);
}

// ---------- Attach event listener to a checkbox ----------

function attachCheckboxListener(cb) {
  if (cb.dataset.compareListenerAttached) return;
  cb.dataset.compareListenerAttached = 'true';
  cb.addEventListener('change', function () {
    compareToggle(this);
  });
  syncCheckbox(cb);
}

// ---------- Watch for dynamically added checkboxes ----------

function observeCheckboxes() {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        // Check if the node itself is a checkbox
        if (node.classList && node.classList.contains('compare-checkbox')) {
          attachCheckboxListener(node);
        }
        // Check descendants
        node.querySelectorAll && node.querySelectorAll('.compare-checkbox').forEach(attachCheckboxListener);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// ---------- Toggle product in/out ----------

function compareToggle(checkbox) {
  const list = getList();
  const product = normalizeCompareProduct({
    id:     checkbox.dataset.productId,
    handle: checkbox.dataset.productHandle,
    title:  checkbox.dataset.productTitle,
    url:    checkbox.dataset.productUrl,
    image:  checkbox.dataset.productImage,
    price:  checkbox.dataset.productPrice,
    type:   checkbox.dataset.productType,
    vendor: checkbox.dataset.productVendor,
  });

  if (!product) {
    checkbox.checked = false;
    return;
  }

  if (checkbox.checked) {
    if (list.length >= COMPARE_LIMIT) {
      alert('You can compare up to ' + COMPARE_LIMIT + ' products at a time.');
      checkbox.checked = false;
      return;
    }
    if (!list.find(p => p.id === product.id)) list.push(product);
  } else {
    const idx = list.findIndex(p => p.id === product.id);
    if (idx > -1) list.splice(idx, 1);
  }

  saveList(list);
  syncAllCheckboxes();
  updateCompareBar();
}

// ---------- Remove a single product ----------

function removeFromCompare(id) {
  const list = getList().filter(p => p.id !== id);
  saveList(list);
  syncAllCheckboxes();
  updateCompareBar();
  if (document.getElementById('compareProductsRow')) renderComparePage();
}

// ---------- Clear all ----------

function clearCompare() {
  saveList([]);
  syncAllCheckboxes();
  updateCompareBar();
  if (document.getElementById('compareProductsRow')) renderComparePage();
}

// ---------- Floating compare bar ----------

function updateCompareBar() {
  const list = getList();
  let bar = document.getElementById('compareBar');

  if (!bar) {
    bar = document.createElement('div');
    bar.id = 'compareBar';
    bar.setAttribute('aria-label', 'Product compare bar');
    bar.innerHTML = `
      <div class="compare-bar-inner page-width">
        <div class="compare-bar-items" id="compareBarItems"></div>
        <div class="compare-bar-actions">
          <span class="compare-bar-count" id="compareBarCount"></span>
          <button class="compare-bar-btn compare-bar-btn--ghost" onclick="clearCompare()">Clear all</button>
          <a href="/pages/product-comparison" class="compare-bar-btn cc-btn compare-bar-btn--primary">Compare now</a>
        </div>
      </div>`;
    document.body.appendChild(bar);
  }

  if (list.length === 0) {
    bar.classList.remove('is-visible');
    return;
  }

  bar.classList.add('is-visible');
  document.getElementById('compareBarCount').textContent = list.length + ' of ' + COMPARE_LIMIT + ' selected';

  document.getElementById('compareBarItems').innerHTML = list.map(p => `
    <div class="compare-bar-item" data-id="${p.id}">
      <img src="${fixImageUrl(p.image)}" alt="${p.title}" width="36" height="36" loading="lazy">
      <span class="compare-bar-item-name">${p.title}</span>
      <button
        class="compare-bar-item-remove"
        onclick="removeFromCompare('${p.id}')"
        aria-label="Remove ${p.title} from compare"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  `).join('');
}

// ---------- Compare page ----------

function initComparePage() {
  const row = document.getElementById('compareProductsRow');
  if (row) {
    renderComparePage();
  } else {
    syncAllCheckboxes();
    updateCompareBar();
  }
  // Always start the observer so late-rendered checkboxes are caught
  observeCheckboxes();
  // Attach to any already-present checkboxes right now
  document.querySelectorAll('.compare-checkbox').forEach(attachCheckboxListener);
}

function renderComparePage() {
  const list = getList();
  const countEl = document.getElementById('compareCount');
  if (countEl) countEl.textContent = '(' + list.length + ' added)';

  const emptyEl   = document.getElementById('compareEmpty');
  const tableWrap = document.querySelector('.compare-table-wrapper');
  const optsRow   = document.querySelector('.compare-options-row');

  if (list.length === 0) {
    document.getElementById('compareProductsRow').innerHTML = '';
    if (tableWrap) tableWrap.style.display = 'none';
    if (optsRow)   optsRow.style.display   = 'none';
    if (emptyEl)   emptyEl.style.display   = 'block';
    return;
  }

  if (emptyEl)   emptyEl.style.display   = 'none';
  if (tableWrap) tableWrap.style.display = '';
  if (optsRow)   optsRow.style.display   = '';

  const slots = list.map(p => `
    <div class="compare-product-card">
      <button
        class="compare-product-card__remove"
        onclick="removeFromCompare('${p.id}')"
        aria-label="Remove ${p.title} from comparison"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="compare-product-card__image">
        <img src="${fixImageUrl(p.image)}" alt="${p.title}" width="160" height="160" loading="lazy">
      </div>
      <h3 class="compare-product-card__title">
        <a href="${p.url}" class="compare-page-product-link" data-product-handle="${p.handle || ''}">${p.title}</a>
      </h3>
      <p class="compare-product-card__price">${p.price}</p>
      <a href="${p.url}" class="cc-btn justify-content-center compare-page-product-link" data-product-handle="${p.handle || ''}">Add to cart</a>
    </div>
  `);

  while (slots.length < COMPARE_LIMIT) {
    slots.push(`
      <div class="compare-product-card compare-product-card--empty">
        <a href="/collections/all" class="compare-product-card__add">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Add product
        </a>
      </div>
    `);
  }

  document.getElementById('compareProductsRow').innerHTML = slots.join('');
  renderCompareTable();
  syncAllCheckboxes();
  updateCompareBar();
}

function getFullDescriptionHtml(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return '';
}

function getCompareDataUrl(productPath) {
  const separator = productPath.includes('?') ? '&' : '?';
  return productPath + separator + 'view=compare-data';
}

function fetchCompareProductData(product) {
  const productPath = getProductComparePath(product);
  if (!productPath) {
    return Promise.reject(new Error('Invalid compare product'));
  }

  return fetch(getCompareDataUrl(productPath))
    .then(function (r) {
      if (!r.ok) throw new Error('Compare data not found');
      return r.json();
    });
}

function renderCompareTable() {
  const list  = document.getElementById('compareTable') ? getList() : [];
  const table = document.getElementById('compareTable');
  if (!table || list.length === 0) { if (table) table.innerHTML = ''; return; }

  const highlight = document.getElementById('highlightDiff') && document.getElementById('highlightDiff').checked;
  const fields = [
    { key: 'type',   label: 'Product type' },
    { key: 'vendor', label: 'Vendor' },
  ];

  let html = '<thead><tr><th class="compare-table__label-col"></th>';
  list.forEach(p => { html += `<th>${p.title}</th>`; });
  html += '</tr></thead><tbody>';

  fields.forEach(f => {
    const vals    = list.map(p => p[f.key] || '—');
    const allSame = vals.every(v => v === vals[0]);
    const cls     = highlight && !allSame ? ' class="is-different"' : '';
    html += `<tr${cls}><td class="compare-table__label">${f.label}</td>`;
    vals.forEach(v => { html += `<td>${v}</td>`; });
    html += '</tr>';
  });

  html += '<tr><td class="compare-table__label">Description</td>';
  list.forEach(p => { html += `<td id="desc-${p.id}"><span class="compare-loading">Loading…</span></td>`; });
  html += '</tr>';

  html += '<tr><td class="compare-table__label">Full Description</td>';
  list.forEach(p => { html += `<td id="full-desc-${p.id}"><span class="compare-loading">Loading…</span></td>`; });
  html += '</tr></tbody>';
  table.innerHTML = html;

  list.forEach(p => {
    fetchCompareProductData(p)
      .then(data => {
        const descEl = document.getElementById('desc-' + p.id);
        if (descEl) {
          if (data.description) {
            const tmp = document.createElement('div');
            tmp.innerHTML = data.description;
            const text = tmp.textContent.trim();
            descEl.textContent = text.slice(0, 200) + (text.length > 200 ? '…' : '');
          } else {
            descEl.textContent = '—';
          }
        }

        const fullDescEl = document.getElementById('full-desc-' + p.id);
        if (fullDescEl) {
          const fullDescHtml = getFullDescriptionHtml(data.full_description);
          if (fullDescHtml) {
            fullDescEl.innerHTML = '<div class="prod-table inner">' + fullDescHtml + '</div>';
          } else {
            fullDescEl.textContent = '—';
          }
        }
      })
      .catch(() => {
        const descEl = document.getElementById('desc-' + p.id);
        if (descEl) descEl.textContent = '—';
        const fullDescEl = document.getElementById('full-desc-' + p.id);
        if (fullDescEl) fullDescEl.textContent = '—';
      });
  });
}

document.addEventListener('DOMContentLoaded', initComparePage);